import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Loading } from '@lmc-app/applications/loading';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { DocusignEnvelopeRecipients, Recipient as EnvelopeRecipient } from '@lmc-app/summary/docusign/interface/docusign-envelope-recipients-interface';

export function addEnvelopeRecipients(arr, ...recipientsToAdd) {
  arr.push(...recipientsToAdd);
  return arr;
}

export function cleanPhone(phoneNumber){
  return phoneNumber?.replace(/[+()# \-.]/g, "");
}

export function mergeDeliveryOptions(defaultOption:boolean,optional:string){
  console.log(defaultOption, optional)
  return defaultOption ? `Email${optional ? `+${optional}` : ''}` : (optional || '');
}

export function addAdditionalDelivery(arr, ...deliveryOption) {
  arr.push(...deliveryOption);
  return arr;
}

export function getFormlyChangedValues(form: FormGroup, initialModel: any): any {
  const result: any = {};

  Object.keys(form.controls).forEach(key => {
    const control = form.get(key);

    // Only process fields that the user actually modified
    if (control && control.dirty) {
      const initialFieldData = initialModel[key] || {};
      let extractedValue = control.value;
      // let appFieldName = initialFieldData.zoho_field_name;    // For zoho changes object
      let appFieldName = initialFieldData[initialModel.DocusignSourceFieldName]   // Original API field name

      // Unwraps the value if Formly wrapped it in an object
      if (extractedValue && typeof extractedValue === 'object' && 'value' in extractedValue) {
        extractedValue = extractedValue.value;
      }
      
      // Reconstruct the object with the new value and existing fields
      // result[key] = {
      //   value: extractedValue,
      //   zoho_field_name: initialFieldData.zoho_field_name
      // };

      
      result[key] = {
        [appFieldName] : ['DOB','CoAppDOB'].includes(key) ? formatZohoCompatibilityDate(extractedValue) : extractedValue
      }
    }
  });

  return result;
}

// Change date format for ZOHO
export function formatZohoCompatibilityDate(extractedValue: any){
  const rawDate = new Date(extractedValue);
  const zohoDate = rawDate.toISOString().split('T')[0];
  return zohoDate;
}

export function isValidData(data:any,excludedList:string[]){
  return Object.entries(data as Record<string, any>)
  .filter(([key]) => !excludedList.includes(key))
  .every(([key, obj]) => {
    const target = obj as { isValid?: boolean };
    return target?.isValid === true;
  });
}

export function createDeliveryChannel(phoneNumber:string, deilveryOption:any){
  return {
    ...(deilveryOption?.deliveryOptions?.includes("+"))
    ?
    {
      additionalNotifications: [
        {
          secondaryDeliveryMethod: deilveryOption?.deliveryOptions?.split('+')?.[1],
          phoneNumber: {
            countryCode: "1",
            number: cleanPhone(phoneNumber)
          }
        }
      ]
    }
    :
    {
      deliveryMethod: deilveryOption?.deliveryOptions,
      phoneNumber: {
        countryCode: "1",
        number: cleanPhone(phoneNumber)
      }
    }
  } 
} 

// Same "1" default createDeliveryChannel() uses on create — a phoneNumber without a
// countryCode isn't usable by DocuSign, so a missing/empty one defaults to "1" here too.
function withDefaultCountryCode<T extends { countryCode?: string; number?: string }>(phoneNumber: T): T {
  return { ...phoneNumber, countryCode: '1' };
  // return { ...phoneNumber, countryCode: phoneNumber?.countryCode || '1' };
}

// Reshapes a recipient loaded from getEnvelopeRecipientStatus (and edited via the
// recipient-list Formly type) into the same DocusignEnvelopeRecipients shape
// createEnvelopeRecipients() sends when the envelope is first created, so the update
// endpoint receives a payload consistent with create.
// Email is the recipient's primary channel unless deliveryMethod was set to 'sms' —
// same rule the recipient-list Formly type uses to decide the Email checkbox state.
function isEmailChannelUsed(r: EnvelopeRecipient): boolean {
  return (r.deliveryMethod || 'email').toLowerCase() !== 'sms';
}

export function mapRecipientForEnvelopeUpdate(r: EnvelopeRecipient, dealName?: string): Partial<DocusignEnvelopeRecipients> {
  return {
    name: r.name,
    email: r.email,
    roleName: r.roleName,
    recipientId: r.recipientId,
    routingOrder: String(r.routingOrder),
    deliveryMethod: r.deliveryMethod,
    ...(isEmailChannelUsed(r) && dealName
      ? {
        emailNotification: {
          emailSubject: `Loan Agreement - ${dealName}`,
          emailBody: `Hi ${r.name?.toUpperCase()},<br><br>Please review and sign, loan agreement of ${dealName} with LM Credit Inc.<br><br>Thank you`,
          supportedLanguage: 'en',
        },
      }
      : {}),
    additionalNotifications: r.additionalNotifications?.map((n) => ({
      ...n,
      ...(n.phoneNumber ? { phoneNumber: withDefaultCountryCode(n.phoneNumber) } : {}),
    })),
    phoneNumber: r.phoneNumber ? withDefaultCountryCode(r.phoneNumber) : r.phoneNumber,
  };
}

/**
 * Check for any changes on Firstname/Middlename/Lastname
 * @param obj 
 * @returns boolean
 */
export function hasRequiredContactChanges(obj) {
  return 'FirstName' in obj || 'LastName' in obj || 'MiddleName' in obj;
}

export function callLoader(_bottomSheet: MatBottomSheet, customMessage: string): MatBottomSheetRef<Loading> {
  return _bottomSheet.open(Loading, {
    disableClose: true,
    data: { message: customMessage },
  });
}

export function openSnackBar(snackBar: MatSnackBar, messageType: string, message: string) {
  const duration = messageType === 'error' ? SnackbarConstants.DURATION_DISMISS_ALIVE : SnackbarConstants.DURATION_DISMISS_SHORT;
  return snackBar.open(message, SnackbarConstants.ACTION_DISMISS, duration);
}

// export function sortTargetSequence(arr, elementName, targetSequence){
//   return arr.sort((a, b) => {
//     return targetSequence.indexOf(a[elementName]?.toLowerCase()) - targetSequence.indexOf(b[elementName]?.toLowerCase());
//   });
// }

interface Recipient {
  name: string;
  email: string;
  roleName: string;
  recipientId: string;
  routingOrder: string;
  [key: string]: any;
}

export function sortTargetSequence(
  arr: Recipient[],
  elementName: string,
  targetSequence: string[]
): Recipient[] {
  const sorted = [...arr].sort((a, b) => {
    const aVal = String(a[elementName] ?? '').toLowerCase();
    const bVal = String(b[elementName] ?? '').toLowerCase();
    return targetSequence.indexOf(aVal) - targetSequence.indexOf(bVal);
  });

  return sorted.map((item, index) => ({
    ...item,
    routingOrder: String(index + 1),
    recipientId: String(index + 1),
  }));
}

function parseAgreementDate(mmddyyyy: string): Date {
  const [mm, dd, yyyy] = mmddyyyy.split('/').map(Number);
  return new Date(yyyy, mm - 1, dd); // local date, avoids UTC off-by-one
}

// Zoho returns date-only fields as 'YYYY-MM-DD'. Handing that string straight to the
// datepicker (or `new Date(str)`) parses it as UTC midnight, which the browser then
// renders in local time — a day behind for any timezone west of UTC. Building the Date
// from its y/m/d parts keeps it anchored to local midnight instead.
export function parseISODateLocal(dateStr: any): Date | null {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const [yyyy, mm, dd] = String(dateStr).split('-').map(Number);
  if (!yyyy || !mm || !dd) return null;
  return new Date(yyyy, mm - 1, dd);
}

// Applies parseISODateLocal to whichever of the given field keys are present on a
// normalized agreement model, so the DOB/CoAppDOB datepickers display the correct day.
/** True when a required (non-optional) model entry has no value. Checks the live value so cleared fields count too */
export function isMissingField(entry: any): boolean {
  const value = entry?.value;
  return entry?.required === true && !!entry?.field_group && entry.field_group !== 'OPTIONAL'
    && (value === '' || value === null || value === undefined);
}

/** Make a field required (asterisk) and touched so it renders red, without the "Required" error text */
export function flagMissingField(field: FormlyFieldConfig): void {
  field.props.required = true;
  field.validation = { ...field.validation, messages: { ...field.validation?.messages, required: () => '' } };
  const control = field.formControl;
  control.addValidators(Validators.required);
  control.updateValueAndValidity({ emitEvent: false });
  control.markAsTouched();
}

export interface MissingField { key: string; missing_display_name: string; field_group: string; }

/** Required model entries that currently have no value */
export function getMissingFields(model: Record<string, any>): MissingField[] {
  return Object.entries(model ?? {})
    .filter(([, f]) => isMissingField(f))
    .map(([key, f]) => ({ key, missing_display_name: f.missing_display_name, field_group: f.field_group }));
}

/** True when a required field that had a value on load has been cleared */
export function hasClearedRequiredFields(missingFields: MissingField[], originalData: Record<string, any>): boolean {
  return missingFields.some(({ key }) => !isMissingField(originalData?.[key]));
}

/** Flag the controls of missing fields as errors. Returns the visible expansion panels holding any of them */
export function flagMissingControls(formlyFields: FormlyFieldConfig[], missingFields: MissingField[]): FormlyFieldConfig[] {
  const missingKeys = new Set(missingFields.map(({ key }) => `${key}.value`));
  return formlyFields
    .filter((panel) => !panel.hide && panel.wrappers?.includes('expansion-panel'))
    .filter((panel) => {
      const fields = (panel.fieldGroup ?? []).filter((f) => missingKeys.has(f.key as string) && f.formControl);
      fields.forEach(flagMissingField);
      return fields.length > 0;
    });
}

/** Open the panels holding missing fields, flag those controls as errors and focus the first one */
export function revealMissingFields(formlyFields: FormlyFieldConfig[], missingFields: MissingField[]): void {
  flagMissingControls(formlyFields, missingFields).forEach((panel, i) => {
    // Only the first panel focuses its invalid control, the rest skip the expand auto-scroll
    panel.props.expandIntent = i === 0 ? 'focusInvalid' : 'silent';
    panel.props.expanded = true;
  });
}

export function localizeDateFields(model: any, keys: string[] = ['DOB', 'CoAppDOB']): void {
  if (!model) return;
  keys.forEach((key) => {
    if (model[key]?.value) {
      model[key] = { ...model[key], value: parseISODateLocal(model[key].value) };
    }
  });
}
